import { useState, useEffect, useRef } from "react"
import { ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { IoMdArrowBack } from "react-icons/io";
// Dark Mode UI is hidden for now (see JSX below) — keeping the import commented
// out instead of removed so it's easy to re-enable later.
// import { useTheme } from "../../context/ThemeContext";
import { saveUserPreferences } from "../../utils/userPreferences";
import { deleteAllRecentSearch } from "../../redux/ApiSlice/SearchSlice";
import { getVideoPreferences, updateVideoPreferences } from "../../redux/ApiSlice/VideoPreferencesSlice";
import toast from "react-hot-toast";

const PreferencesPage = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    // const { theme, toggleTheme } = useTheme();
    const pageRef = useRef(null);
    const scrollContainerRef = useRef(null);
    // const [isDarkMode, setIsDarkMode] = useState(theme === "dark");
    const { autoplay, mute_video, loaded } = useSelector((state) => state.VideoPreferences);

    // Using an object to manage all checkbox states
    const [preferences, setPreferences] = useState({
        enableAutoplay: false,
        muteVideosByDefault: false,
        hideSensitiveContent: false,
        allowDataUsage: false,
        hideOwnLikeCount: false
    });
    const [saveLoading, setSaveLoading] = useState(false);
    const [clearLoading, setClearLoading] = useState(false);
    const [showConfirmReset, setShowConfirmReset] = useState(false);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [modalViewport, setModalViewport] = useState({ top: 0, height: null });

    // Dark Mode toggle is hidden for now (see JSX below). Keeping this logic
    // commented out instead of removed so it's easy to re-enable later.
    // useEffect(() => {
    //     setIsDarkMode(theme === "dark");
    // }, [theme]);

    // const handleThemeToggle = () => {
    //     setIsDarkMode(prev => !prev);
    //     toggleTheme();
    // };

    // Fetch the latest video preferences from the profile/video-preferences API on mount
    useEffect(() => {
        dispatch(getVideoPreferences());
    }, [dispatch]);

    // Keep the checkbox state in sync with the redux-backed preferences
    useEffect(() => {
        if (!loaded) {
            return;
        }
        setPreferences(prev => ({
            ...prev,
            enableAutoplay: autoplay,
            muteVideosByDefault: mute_video,
            hideOwnLikeCount: preferences.hideOwnLikeCount, // Default value, update as needed
        }));
    }, [loaded, autoplay, mute_video]);

    useEffect(() => {
        if (!showConfirmReset) {
            return;
        }

        const preventScroll = (event) => {
            event.preventDefault();
        };

        window.addEventListener("wheel", preventScroll, { passive: false });
        window.addEventListener("touchmove", preventScroll, { passive: false });

        return () => {
            window.removeEventListener("wheel", preventScroll);
            window.removeEventListener("touchmove", preventScroll);
        };
    }, [showConfirmReset]);

    useEffect(() => {
        const pageElement = pageRef.current;
        if (!pageElement) {
            return;
        }

        const scrollContainer = pageElement.closest(".overflow-y-scroll");
        scrollContainerRef.current = scrollContainer;
        if (!scrollContainer || !showConfirmReset) {
            return;
        }

        const originalOverflow = scrollContainer.style.overflow;
        const originalOverflowY = scrollContainer.style.overflowY;
        const originalTouchAction = scrollContainer.style.touchAction;

        scrollContainer.style.overflow = "hidden";
        scrollContainer.style.overflowY = "hidden";
        scrollContainer.style.touchAction = "none";

        return () => {
            scrollContainer.style.overflow = originalOverflow;
            scrollContainer.style.overflowY = originalOverflowY;
            scrollContainer.style.touchAction = originalTouchAction;
        };
    }, [showConfirmReset]);

    // Generic handler for all checkboxes
    const handleCheckboxChange = (key) => {
        setPreferences(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    }

    // Hide My Like Count saves to localStorage immediately (instead of waiting
    // for the "Save changes" button) so it isn't lost if the user navigates
    // away before saving.
    const handleHideLikeCountToggle = () => {
        setPreferences(prev => {
            const nextValue = !prev.hideOwnLikeCount;
            saveUserPreferences({ hideOwnLikeCount: nextValue });
            return { ...prev, hideOwnLikeCount: nextValue };
        });
    }

    const handleSaveChanges = async () => {
        setSaveLoading(true);

        // Create a minimum delay promise of 2 seconds
        const minDelay = new Promise(resolve => setTimeout(resolve, 2000));

        const saveOperation = dispatch(updateVideoPreferences({
            autoplay: preferences.enableAutoplay,
            mute_video: preferences.muteVideosByDefault
        })).unwrap();

        try {
            await Promise.all([saveOperation, minDelay]);

            // Mirror into localStorage so other surfaces (Home, Reels) stay in sync
            saveUserPreferences({
                enableAutoplay: preferences.enableAutoplay,
                muteVideosByDefault: preferences.muteVideosByDefault,
                hideOwnLikeCount: preferences.hideOwnLikeCount
            });

            toast.success("Preferences saved successfully!");
            setShowSuccessMessage(true);
            setTimeout(() => {
                setShowSuccessMessage(false);
            }, 4000);
        } catch {
            toast.error("Failed to save preferences");
        }
        setSaveLoading(false);
    };

    const closeConfirm = () => setShowConfirmReset(false);

    const handleConfirmReset = async () => {
        setClearLoading(true);
        try {
            await dispatch(deleteAllRecentSearch()).unwrap();
            toast.success("Search history cleared");
        } catch {
            toast.error("Failed to clear search history");
        }
        setClearLoading(false);
        closeConfirm();
    };
    const preventModalScroll = (event) => event.preventDefault();
    const openConfirmReset = () => {
        const pageElement = pageRef.current;
        const scrollContainer = scrollContainerRef.current || pageElement?.closest(".overflow-y-scroll");

        if (scrollContainer) {
            setModalViewport({
                top: scrollContainer.scrollTop,
                height: scrollContainer.clientHeight,
            });
        }

        setShowConfirmReset(true);
    };

    return (
        <div ref={pageRef} className="h-full bg-white dark:text-white dark:bg-[#1E1D21] mb-5 relative">
            <div className="sticky top-0 z-50">
                <div className="relative flex items-center justify-center h-12 bg-[#BDE6FF] rounded-b-xl px-2">
                    <IoMdArrowBack
                        onClick={() => navigate("/account/settings")}
                        className="absolute left-2 top-1/2 transition-transform -translate-y-1/2 text-[22px] text-black cursor-pointer"
                    />
                    <p className="text-center font-bold text-[16px] text-black flex-grow">Preferences</p>
                </div>
            </div>

            <div className="px-3 pb-3 flex flex-col justify-between">
                <div className="flex flex-col pt-3">
                    <p className="text-[14px] text-black dark:text-white font-bold">Your Preferences</p>
                    <p className="text-[11px] font-normal pt-1">
                        Control what you see across Chaa Ching . To see changes, refresh the page,
                        or restart Chaa Ching  on your devices. Completing this information helps us give you a better user experience.
                    </p>
                    <div className="mt-4">
                        <p className="text-[14px] text-black dark:text-white font-bold">Content preferences</p>
                        <div
                            onClick={() => navigate("/settings/content-preferences/filter-keywords")}
                            className="mt-2 flex items-center justify-between gap-x-3 border-2 border-[#BDE6FF] dark:border-gray-800/50 rounded-lg px-3 py-2"
                        >
                            <div>
                                <p className="text-[13px] text-start font-bold">Filter Keywords</p>
                                <p className="text-[10px] font-normal mt-1 w-60">
                                    Manage your filter keywords. Content containing these
                                    keywords will be hidden from your feed and search results.
                                </p>
                            </div>
                            <ChevronRight className="w-4 h-4 cursor-pointer text-black dark:text-white" />
                        </div>
                        <div className="mt-2 border-2 border-[#BDE6FF] dark:border-gray-800/50 rounded-lg">
                            <div className="flex justify-between gap-x-14 px-3 py-2">
                                <div>
                                    <p className="text-[14px] font-bold">Hide My Like Count</p>
                                    <p className="mt-1 text-[10px] font-normal">Hide the like count on your own posts and videos from yourself across Chaa Ching.</p>
                                </div>
                                <label className="autoSaverSwitch relative inline-flex cursor-pointer select-none items-center">
                                    <input
                                        type="checkbox"
                                        name="hideOwnLikeCount"
                                        className="sr-only"
                                        checked={preferences.hideOwnLikeCount}
                                        onChange={handleHideLikeCountToggle}
                                    />
                                    <span className={`slider flex h-[19px] w-[40px] items-center rounded-full p-1 duration-200 ${preferences.hideOwnLikeCount ? "bg-[#BDE6FF]" : "bg-[#d9d9d9]"}`}>
                                        <span className={`dot h-[13px] w-[13px] rounded-full bg-[#666666] shadow-2xl duration-200 ${preferences.hideOwnLikeCount ? "translate-x-5" : ""}`} />
                                    </span>
                                </label>
                            </div>
                        </div>
                    </div>
                    {/* Dark Mode is hidden for now — keeping the block commented out instead
                        of removed so it's easy to re-enable later.
                    <div className="mt-4">
                        <p className="text-[14px] text-black dark:text-white font-bold">Display preferences</p>
                        <div className="mt-2 border-2 border-[#BDE6FF] dark:border-gray-800/50 rounded-lg">
                            <div className="flex justify-between gap-x-14 px-3 py-2">
                                <div>
                                    <p className="text-[14px] font-bold">Dark Mode</p>
                                    <p className="mt-1 text-[10px] font-normal">Switch between light and dark appearance.</p>
                                </div>
                                <div
                                    className="autoSaverSwitch relative inline-flex cursor-pointer select-none items-center"
                                    onClick={handleThemeToggle}
                                >
                                    <span className={`slider flex h-[19px] w-[40px] items-center rounded-full p-1 duration-200 ${isDarkMode ? "bg-[#BDE6FF]" : "bg-[#d9d9d9]"}`}>
                                        <span className={`dot h-[13px] w-[13px] rounded-full bg-[#666666] shadow-2xl duration-200 ${isDarkMode ? "translate-x-5" : ""}`} />
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                    */}
                    <div className="mt-4">
                        <p className="text-[14px] text-black dark:text-white font-bold">Video preferences</p>
                        <div className="mt-2 border-2 border-[#BDE6FF] dark:border-gray-800/50 rounded-lg">
                            <div className="flex justify-between gap-x-14 px-3 py-2 border-b-2 border-[#BDE6FF] dark:border-gray-800/50">
                                <div>
                                    <p className="text-[14px] font-bold">Enable Autoplay</p>
                                    <p className="mt-1 text-[10px] font-normal">Autoplay the next related item when a file (video or audio) finishes playing.</p>
                                </div>
                                <label className="autoSaverSwitch relative inline-flex cursor-pointer select-none items-center">
                                    <input
                                        type="checkbox"
                                        name="enableAutoplay"
                                        className="sr-only"
                                        checked={preferences.enableAutoplay}
                                        onChange={() => handleCheckboxChange('enableAutoplay')}
                                    />
                                    <span className={`slider flex h-[19px] w-[40px] items-center rounded-full p-1 duration-200 ${preferences.enableAutoplay ? "bg-[#BDE6FF]" : "bg-[#d9d9d9]"}`}>
                                        <span className={`dot h-[13px] w-[13px] rounded-full ${preferences.enableAutoplay ? "bg-[#666666]"
                                            : "bg-[#666666]"} shadow-2xl duration-200 ${preferences.enableAutoplay ? "translate-x-5" : ""}
                                    `} />
                                    </span>
                                </label>
                            </div>
                            <div className="flex justify-between gap-x-14 px-3 py-2">
                                <div>
                                    <p className="text-[14px] font-bold">Mute Video</p>
                                    <p className="mt-1 text-[10px] font-normal text-justify">Mute Videos by default</p>
                                </div>
                                <label className="autoSaverSwitch relative inline-flex cursor-pointer select-none items-center">
                                    <input
                                        type="checkbox"
                                        name="muteVideosByDefault"
                                        className="sr-only"
                                        checked={preferences.muteVideosByDefault}
                                        onChange={() => handleCheckboxChange('muteVideosByDefault')}
                                    />
                                    <span className={`slider flex h-[19px] w-[40px] items-center rounded-full p-1 duration-200 ${preferences.muteVideosByDefault ? "bg-[#BDE6FF]" : "bg-[#d9d9d9]"}`}>
                                        <span className={`dot h-[13px] w-[13px] rounded-full ${preferences.muteVideosByDefault ? "bg-[#666666]"
                                            : "bg-[#666666]"} shadow-2xl duration-200 ${preferences.muteVideosByDefault ? "translate-x-5" : ""}
                                    `} />
                                    </span>
                                </label>
                            </div>
                        </div>
                    </div>
                    <div className="mt-6">
                        <p className="text-[14px] text-black dark:text-white font-bold">Search preferences</p>
                        <div
                            onClick={openConfirmReset}
                            className="mt-2 flex items-center justify-between gap-x-3 border-2 border-[#BDE6FF] dark:border-gray-800/50 rounded-lg px-3 py-2 cursor-pointer"
                        >
                            <div>
                                <p className="text-[13px] text-start font-bold">Reset Search history</p>
                                <p className="text-[10px] font-normal mt-1 w-60">
                                    Clear your recently viewed search results and we'll no longer
                                    show them on the site. Future searches will still be recorded.
                                </p>
                            </div>
                            <ChevronRight className="w-4 h-4 cursor-pointer text-black dark:text-white" />
                        </div>
                    </div>
                </div>
                <button
                    className="mt-3 text-[13px] w-full bg-[#BDE6FF] text-black font-bold py-3 rounded-lg"
                    onClick={handleSaveChanges}
                >
                    {saveLoading ? "Saving..." : "Save changes"}
                </button>
            </div>

            {showConfirmReset && (
                <div
                    onClick={closeConfirm}
                    onWheel={preventModalScroll}
                    onTouchMove={preventModalScroll}
                    style={{
                        top: modalViewport.top,
                        height: modalViewport.height || "100%",
                    }}
                    className="absolute left-0 right-0 bg-black bg-opacity-50 flex items-center justify-center z-[200]"
                >
                    <div
                        className="w-full max-w-sm mx-4 rounded-2xl bg-white p-5 shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <p className="text-center text-[13px] font-semibold text-black">
                            Are you sure you want to reset your search history?
                        </p>
                        <div className="mt-4 flex justify-between gap-3">
                            <button
                                className="w-1/2 rounded-full border border-[#BDE6FF] bg-white py-1 text-[12px] font-semibold text-[#3b3b3b]"
                                onClick={closeConfirm}
                            >
                                Cancel
                            </button>
                            <button
                                className="w-1/2 rounded-full bg-[#BDE6FF] py-1 text-[12px] font-semibold text-black flex items-center justify-center gap-1"
                                onClick={handleConfirmReset}
                                disabled={clearLoading}
                            >
                                {clearLoading ? (
                                    <svg className="animate-spin h-3.5 w-3.5 text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                    </svg>
                                ) : "Confirm"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default PreferencesPage